import { lazy, Suspense } from 'react';
import { createBrowserRouter, Navigate, Outlet } from 'react-router-dom';
import { RequireAuth, RequirePermission, RequirePlatform } from './components/guards.js';
import { Spinner } from './components/ui/spinner.js';

/** Route bundles are lazy-loaded so first paint stays under 2s on 4G (NFR-03). */
const LoginPage = lazy(() => import('./features/auth/pages/login.js'));
const ActivatePage = lazy(() => import('./features/auth/pages/activate.js'));
const ForgotPasswordPage = lazy(() => import('./features/auth/pages/forgot-password.js'));
const ResetPasswordPage = lazy(() => import('./features/auth/pages/reset-password.js'));

const EmployeeShell = lazy(() => import('./apps/employee/shell.js'));
const EmployeeHome = lazy(() => import('./apps/employee/home.js'));
const ApprovalsPage = lazy(() => import('./apps/employee/approvals.js'));
const DirectoryPage = lazy(() => import('./apps/employee/directory.js'));
const MyDocumentsPage = lazy(() => import('./apps/employee/documents.js'));
const LeavePage = lazy(() => import('./apps/employee/leave.js'));
const AttendancePage = lazy(() => import('./apps/employee/attendance.js'));
const PayslipsPage = lazy(() => import('./apps/employee/payslips.js'));
const TaxPage = lazy(() => import('./apps/employee/tax.js'));
const ExpensesPage = lazy(() => import('./apps/employee/expenses.js'));
const MyAssetsPage = lazy(() => import('./apps/employee/assets.js'));
const HelpdeskPage = lazy(() => import('./apps/employee/helpdesk.js'));
const PoliciesPage = lazy(() => import('./apps/employee/policies.js'));
const PerformancePage = lazy(() => import('./apps/employee/performance.js'));
const InterviewsPage = lazy(() => import('./apps/employee/interviews.js'));
const NoticeboardPage = lazy(() => import('./apps/employee/noticeboard.js'));
const LearningPage = lazy(() => import('./apps/employee/learning.js'));
const GrievancesPage = lazy(() => import('./apps/employee/grievances.js'));
const CareersPage = lazy(() => import('./apps/public/careers.js'));
const OfferPage = lazy(() => import('./apps/public/offer.js'));

const AdminShell = lazy(() => import('./apps/admin/shell.js'));
const AdminHome = lazy(() => import('./apps/admin/home.js'));
const SupportAccessPage = lazy(() => import('./apps/admin/support-access.js'));
const OrgPage = lazy(() => import('./apps/admin/org.js'));
const OrgChartPage = lazy(() => import('./apps/admin/org-chart.js'));
const EmployeesPage = lazy(() => import('./apps/admin/employees.js'));
const NewEmployeePage = lazy(() => import('./apps/admin/employee-new.js'));
const EmployeeDetailPage = lazy(() => import('./apps/admin/employee-detail.js'));
const ReportsPage = lazy(() => import('./apps/admin/reports.js'));
const ImportPage = lazy(() => import('./apps/admin/import.js'));
const ApprovalFlowsPage = lazy(() => import('./apps/admin/approval-flows.js'));
const AuditPage = lazy(() => import('./apps/admin/audit.js'));
const LeaveAdminPage = lazy(() => import('./apps/admin/leave.js'));
const AttendanceAdminPage = lazy(() => import('./apps/admin/attendance.js'));
const PayrollAdminPage = lazy(() => import('./apps/admin/payroll.js'));
const BillingAdminPage = lazy(() => import('./apps/admin/billing.js'));
const ExpensesAdminPage = lazy(() => import('./apps/admin/expenses.js'));
const AssetsAdminPage = lazy(() => import('./apps/admin/assets.js'));
const HelpdeskAdminPage = lazy(() => import('./apps/admin/helpdesk.js'));
const LibraryAdminPage = lazy(() => import('./apps/admin/library.js'));
const PerformanceAdminPage = lazy(() => import('./apps/admin/performance.js'));
const RecruitmentAdminPage = lazy(() => import('./apps/admin/recruitment.js'));
const EngagementAdminPage = lazy(() => import('./apps/admin/engagement.js'));
const LearningAdminPage = lazy(() => import('./apps/admin/learning.js'));
const SupportSessionPage = lazy(() => import('./features/support/support-session.js'));

const PlatformLoginPage = lazy(() => import('./apps/platform/login.js'));
const PlatformActivatePage = lazy(() => import('./apps/platform/activate.js'));
const PlatformShell = lazy(() => import('./apps/platform/shell.js'));
const CompanyListPage = lazy(() => import('./apps/platform/companies.js'));
const CompanyCreatePage = lazy(() => import('./apps/platform/company-create.js'));
const MigrationsPage = lazy(() => import('./apps/platform/migrations.js'));
const CompanyDetailPage = lazy(() => import('./apps/platform/company-detail.js'));
const StatutoryPage = lazy(() => import('./apps/platform/statutory.js'));
const PlatformBillingPage = lazy(() => import('./apps/platform/billing.js'));

function Loading(): JSX.Element {
  return (
    <div className="flex min-h-screen items-center justify-center">
      <Spinner label="Loading" />
    </div>
  );
}

/** A child route shown only with one of the permissions (UX only; the API checks too). */
function guarded(path: string, anyOf: string[], element: JSX.Element) {
  return { path, element: <RequirePermission anyOf={anyOf}>{element}</RequirePermission> };
}

function Boundary(): JSX.Element {
  return (
    <Suspense fallback={<Loading />}>
      <Outlet />
    </Suspense>
  );
}

export const router = createBrowserRouter([
  {
    element: <Boundary />,
    children: [
      // --- public auth routes ---
      { path: '/login', element: <LoginPage /> },
      { path: '/activate', element: <ActivatePage /> },
      { path: '/forgot-password', element: <ForgotPasswordPage /> },
      { path: '/reset-password', element: <ResetPasswordPage /> },
      // FR-CP-14 — a support session arrives here from the platform console.
      { path: '/support-session', element: <SupportSessionPage /> },
      // FR-RC-03, 08 — public, no sign-in: the careers page and a candidate's offer.
      { path: '/careers/:slug', element: <CareersPage /> },
      { path: '/careers/:slug/:jobId', element: <CareersPage /> },
      { path: '/offer/:slug/:token', element: <OfferPage /> },

      // --- employee self-service ---
      {
        path: '/',
        element: (
          <RequireAuth>
            <EmployeeShell />
          </RequireAuth>
        ),
        children: [
          { index: true, element: <EmployeeHome /> },
          { path: 'approvals', element: <ApprovalsPage /> },
          { path: 'directory', element: <DirectoryPage /> },
          { path: 'documents', element: <MyDocumentsPage /> },
          { path: 'leave', element: <LeavePage /> },
          { path: 'attendance', element: <AttendancePage /> },
          { path: 'payslips', element: <PayslipsPage /> },
          { path: 'payslips/:id', element: <PayslipsPage /> },
          { path: 'tax', element: <TaxPage /> },
          { path: 'expenses', element: <ExpensesPage /> },
          { path: 'assets', element: <MyAssetsPage /> },
          { path: 'helpdesk', element: <HelpdeskPage /> },
          { path: 'policies', element: <PoliciesPage /> },
          { path: 'performance', element: <PerformancePage /> },
          { path: 'interviews', element: <InterviewsPage /> },
          { path: 'noticeboard', element: <NoticeboardPage /> },
          { path: 'learning', element: <LearningPage /> },
          { path: 'grievances', element: <GrievancesPage /> },
        ],
      },

      // --- company admin ---
      {
        path: '/admin',
        element: (
          <RequireAuth>
            <RequirePermission
              anyOf={['employee.read', 'report.read', 'org.read', 'company.configure', 'payroll.read', 'expense.read', 'expense.pay', 'asset.read', 'helpdesk.read', 'helpdesk.manage', 'library.manage', 'performance.read', 'performance.manage', 'recruitment.read', 'recruitment.manage', 'engagement.manage', 'learning.read', 'learning.manage']}
            >
              <AdminShell />
            </RequirePermission>
          </RequireAuth>
        ),
        children: [
          { index: true, element: <AdminHome /> },
          guarded('employees', ['employee.read'], <EmployeesPage />),
          guarded('employees/new', ['employee.create'], <NewEmployeePage />),
          guarded('employees/import', ['employee.create'], <ImportPage />),
          guarded('employees/:id', ['employee.read'], <EmployeeDetailPage />),
          guarded('employees/:id/:tab', ['employee.read'], <EmployeeDetailPage />),
          guarded('reports', ['report.read'], <ReportsPage />),
          guarded('org', ['org.read'], <OrgPage />),
          guarded('org-chart', ['employee.read'], <OrgChartPage />),
          guarded('approval-flows', ['company.configure'], <ApprovalFlowsPage />),
          guarded('audit', ['audit.read'], <AuditPage />),
          guarded('leave', ['leave.configure', 'leave.read', 'leave.adjust'], <LeaveAdminPage />),
          guarded('attendance', ['attendance.read', 'attendance.configure'], <AttendanceAdminPage />),
          guarded('payroll', ['payroll.read'], <PayrollAdminPage />),
          guarded('billing', ['company.configure'], <BillingAdminPage />),
          guarded('expenses', ['expense.read', 'expense.pay', 'expense.configure'], <ExpensesAdminPage />),
          guarded('assets', ['asset.read', 'asset.manage'], <AssetsAdminPage />),
          guarded('helpdesk', ['helpdesk.read', 'helpdesk.manage'], <HelpdeskAdminPage />),
          guarded('library', ['library.manage'], <LibraryAdminPage />),
          guarded('performance', ['performance.read', 'performance.manage'], <PerformanceAdminPage />),
          guarded('recruitment', ['recruitment.read', 'recruitment.manage'], <RecruitmentAdminPage />),
          guarded('engagement', ['engagement.manage', 'company.configure'], <EngagementAdminPage />),
          guarded('learning', ['learning.read', 'learning.manage'], <LearningAdminPage />),
          {
            path: 'support-access',
            element: (
              <RequirePermission anyOf={['support.approve']}>
                <SupportAccessPage />
              </RequirePermission>
            ),
          },
        ],
      },

      // --- platform console ---
      { path: '/platform/login', element: <PlatformLoginPage /> },
      { path: '/platform/activate', element: <PlatformActivatePage /> },
      {
        path: '/platform',
        element: (
          <RequirePlatform>
            <PlatformShell />
          </RequirePlatform>
        ),
        children: [
          { index: true, element: <Navigate to="/platform/companies" replace /> },
          { path: 'companies', element: <CompanyListPage /> },
          { path: 'companies/new', element: <CompanyCreatePage /> },
          { path: 'companies/:id', element: <CompanyDetailPage /> },
          { path: 'companies/:id/:tab', element: <CompanyDetailPage /> },
          { path: 'migrations', element: <MigrationsPage /> },
          { path: 'statutory', element: <StatutoryPage /> },
          { path: 'billing', element: <PlatformBillingPage /> },
        ],
      },

      { path: '*', element: <Navigate to="/" replace /> },
    ],
  },
]);
